import { Link, usePage } from '@inertiajs/react'
import { Divider, Group, Loader, NavLink, Stack, Text, ThemeIcon } from '@mantine/core'
import { Clock } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNotifications } from '@/lib/useNotifications'
import type { ActiveSessionProps, SharedProps } from '@/types'

const ACTIVE_STATUSES = ['queued', 'running', 'waiting_for_user']

function StatusIcon({ status }: { status: ActiveSessionProps['status'] }) {
  if (status === 'waiting_for_user') {
    return (
      <ThemeIcon size="xs" variant="transparent" color="yellow">
        <Clock size={14} />
      </ThemeIcon>
    )
  }
  return (
    <ThemeIcon size="xs" variant="transparent" color="blue">
      <Loader size={14} />
    </ThemeIcon>
  )
}

export default function ActiveSessionsList() {
  const { active_sessions: initialSessions } = usePage<SharedProps>().props
  const [sessions, setSessions] = useState<ActiveSessionProps[]>(initialSessions ?? [])

  // Sync with server-provided props on Inertia navigation
  useEffect(() => {
    setSessions(initialSessions ?? [])
  }, [initialSessions])

  useNotifications(
    useCallback((data: Record<string, unknown>) => {
      if (data.type === 'active_session_changed') {
        const session = data.session as ActiveSessionProps
        setSessions((prev) => {
          if (ACTIVE_STATUSES.includes(session.status)) {
            const exists = prev.some((s) => s.id === session.id)
            if (exists) {
              return prev.map((s) => (s.id === session.id ? session : s))
            }
            return [session, ...prev]
          }
          return prev.filter((s) => s.id !== session.id)
        })
      }

      if (data.type === 'session_title_updated') {
        const sessionId = data.session_id as string
        const title = data.title as string
        setSessions((prev) =>
          prev.map((s) => (s.id === sessionId ? { ...s, title } : s))
        )
      }
    }, [])
  )

  if (sessions.length === 0) return null

  return (
    <>
      <Divider />
      <Stack gap={2} p="xs">
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" px="xs" pb={4}>
          Active Sessions
        </Text>
        {sessions.map((session) => (
          <NavLink
            key={session.id}
            component={Link}
            href={`/projects/${session.project_id}/chats?session=${session.id}`}
            label={
              <Text size="sm" truncate>
                {session.title || session.agent_name}
              </Text>
            }
            description={
              <Text size="xs" c="dimmed" truncate>
                {session.project_name}
              </Text>
            }
            leftSection={<StatusIcon status={session.status} />}
            py={4}
          />
        ))}
      </Stack>
    </>
  )
}
